import type { ReactElement } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { AccentCard } from './AccentCard';
import { InfoTooltip } from './InfoTooltip';
import {
  SIGNAL_BAR_CLASSES,
  type ToolCallScanEvidence,
  additionalSignalChips,
  categorySignalRows,
  deriveToolCallVerdict,
  formatToolCallScore,
  formatToolCallScoreAgainstThreshold,
  formatToolCallThreshold,
  humanizeSignalLabel,
} from './tool-call-detail.helpers';
import {
  TOOL_CALL_LABELS,
  TOOL_CALL_TOOLTIPS,
  TOOL_CALL_VERDICT_PILL,
  categorySignalTooltip,
} from './tool-call-copy';

interface ToolCallVerdictCardProps {
  evidence?: ToolCallScanEvidence;
  /** The per-finding detection reason (`alert.description`). */
  reason?: string | null;
}

/** Percentage position on a 0–1 scale, clamped so a stray score stays in the bar. */
function toPercent(value: number): number {
  return Math.max(0, Math.min(100, value * 100));
}

/**
 * KIR-4836 — "Why Kirin AI flagged this": the reason in words, the one number
 * that decided the verdict, and every remaining number behind a toggle.
 *
 * Replaces the generic Detection Logic rendering for this type, which listed
 * `Class scores`, `Verdict` and `Session context` as flat key/value rows with
 * no ordering cue, no threshold comparison and no explanation of any term.
 */
export function ToolCallVerdictCard({
  evidence,
  reason,
}: ToolCallVerdictCardProps): ReactElement {
  const verdict = deriveToolCallVerdict(evidence);
  const rows = categorySignalRows(evidence);
  const chips = additionalSignalChips(evidence);
  const malScore = evidence?.malScore;
  const threshold = evidence?.threshold;
  const topThreatLabel = evidence?.topThreatLabel;
  const trimmedReason = reason?.trim();
  /* The cutoff is 0.496 today, so both numbers are rendered against each other
     rather than at a flat 2 decimals -- see the helpers for why. */
  const thresholdText =
    threshold === undefined ? undefined : formatToolCallThreshold(threshold);
  const malScoreText =
    malScore === undefined
      ? undefined
      : threshold === undefined
        ? formatToolCallScore(malScore)
        : formatToolCallScoreAgainstThreshold(malScore, threshold);
  const hasDetails = rows.length > 0 || chips.length > 0;
  const hasFlaggedRow = rows.some(row => row.flagged);

  return (
    <AccentCard category="why-flagged" title={TOOL_CALL_LABELS.verdictCard}>
      {topThreatLabel !== undefined && (
        <p className="text-sm font-semibold" data-testid="tool-call-headline">
          {verdict === 'flagged' ? 'Flagged as ' : 'Top category: '}
          <span
            className={cn(
              verdict === 'flagged' && 'text-rose-600 dark:text-rose-400'
            )}
          >
            {humanizeSignalLabel(topThreatLabel)}
          </span>
        </p>
      )}
      {trimmedReason !== undefined && trimmedReason.length > 0 && (
        <p className="mt-1 text-sm text-muted-foreground">{trimmedReason}</p>
      )}

      {malScore !== undefined && (
        <div className="mt-3 rounded-md border p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              {TOOL_CALL_LABELS.maliciousScore}
              <InfoTooltip
                content={TOOL_CALL_TOOLTIPS.maliciousScore}
                label={TOOL_CALL_LABELS.maliciousScore}
              />
            </span>
            <span className="text-xs tabular-nums">
              <span
                className={cn(
                  'font-semibold',
                  verdict === 'flagged' && 'text-rose-600 dark:text-rose-400'
                )}
              >
                {malScoreText}
              </span>
              {thresholdText !== undefined && (
                <span className="text-muted-foreground">
                  {' / '}
                  {thresholdText} threshold
                </span>
              )}
            </span>
          </div>

          {/* `mt-6` leaves room for the floating threshold caption above the track. */}
          <div className="relative mt-6">
            {threshold !== undefined && (
              <span
                className="absolute -top-4 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground"
                style={{ left: `${toPercent(threshold)}%` }}
              >
                threshold {thresholdText}
              </span>
            )}
            <div
              className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label={TOOL_CALL_LABELS.maliciousScore}
              aria-valuenow={malScore}
              aria-valuemin={0}
              aria-valuemax={1}
            >
              <div
                className={cn(
                  'h-full rounded-full',
                  verdict === 'flagged'
                    ? 'bg-gradient-to-r from-orange-400 to-red-500'
                    : 'bg-slate-400 dark:bg-slate-500'
                )}
                style={{ width: `${toPercent(malScore)}%` }}
              />
              {threshold !== undefined && (
                <div
                  aria-hidden
                  className="absolute inset-y-0 w-px bg-foreground"
                  style={{ left: `${toPercent(threshold)}%` }}
                />
              )}
            </div>
          </div>

          {verdict !== null && (
            <span
              data-testid="tool-call-verdict-pill"
              className={cn(
                'mt-3 inline-flex rounded-full border px-2.5 py-0.5 text-xs',
                verdict === 'flagged'
                  ? 'border-red-500/40 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  : 'border-border bg-muted text-muted-foreground'
              )}
            >
              {TOOL_CALL_VERDICT_PILL[verdict]}
            </span>
          )}
        </div>
      )}

      {hasDetails && (
        /* Collapsed by default — the ticket's whole premise is that the raw
           numbers sit behind "Show detection details" rather than leading. */
        <details
          className="group mt-3"
          data-testid="tool-call-detection-details"
        >
          <summary className="inline-flex cursor-pointer select-none items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-primary [&::-webkit-details-marker]:hidden">
            <span className="group-open:hidden">
              {TOOL_CALL_LABELS.showDetails}
            </span>
            <span className="hidden group-open:inline">
              {TOOL_CALL_LABELS.hideDetails}
            </span>
            <ChevronDown className="h-4 w-4 group-open:hidden" aria-hidden />
            <ChevronUp
              className="hidden h-4 w-4 group-open:block"
              aria-hidden
            />
          </summary>

          <div className="mt-3 rounded-md border border-dashed p-3">
            {rows.length > 0 && (
              <>
                <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {TOOL_CALL_LABELS.categorySignals}
                  <InfoTooltip
                    content={TOOL_CALL_TOOLTIPS.categorySignals}
                    label={TOOL_CALL_LABELS.categorySignals}
                  />
                </p>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {rows.map(row => (
                    <li
                      key={row.key}
                      className={cn(
                        'grid items-center gap-2',
                        // The FLAGGED badge gets its own fixed-width column so
                        // every bar starts on the same line — an `auto` column
                        // collapses on the unflagged rows and steps their bars
                        // left of the flagged one. The column is dropped
                        // entirely when no row is flagged, so an all-clear
                        // panel carries no empty gutter.
                        hasFlaggedRow
                          ? 'grid-cols-[minmax(6rem,9rem)_5rem_1fr_auto]'
                          : 'grid-cols-[minmax(6rem,9rem)_1fr_auto]'
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-1 text-xs">
                        <span className="truncate" title={row.label}>
                          {row.label}
                        </span>
                        <InfoTooltip
                          content={categorySignalTooltip(row.label)}
                          label={row.label}
                          className="[&>svg]:h-3 [&>svg]:w-3"
                        />
                      </span>
                      {hasFlaggedRow && (
                        <span>
                          {row.flagged && (
                            <Badge
                              variant="destructive"
                              className="px-1.5 py-0 text-[10px] uppercase"
                              data-testid="category-signal-flagged"
                            >
                              {TOOL_CALL_LABELS.flagged}
                            </Badge>
                          )}
                        </span>
                      )}
                      <div
                        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                        role="progressbar"
                        aria-label={row.label}
                        aria-valuenow={row.score}
                        aria-valuemin={0}
                        aria-valuemax={1}
                      >
                        <div
                          className={cn(
                            'h-full rounded-full',
                            SIGNAL_BAR_CLASSES[row.tone]
                          )}
                          style={{ width: `${row.percent}%` }}
                        />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {row.scoreText}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {chips.length > 0 && (
              <>
                <p
                  className={cn(
                    'flex flex-wrap items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                    rows.length > 0 && 'mt-4'
                  )}
                >
                  {TOOL_CALL_LABELS.additionalSignals}
                  <span className="font-normal normal-case">
                    {TOOL_CALL_LABELS.additionalSignalsHint}
                  </span>
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {chips.map(chip => (
                    <span
                      key={chip.key}
                      data-testid={`additional-signal-${chip.key}`}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                        chip.danger
                          ? 'border-red-500/40 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                          : // Same slate step the evidence `<pre>` uses, so the
                            // chip reads as a raised surface instead of melting
                            // into the card behind it in dark mode.
                            'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200'
                      )}
                    >
                      {chip.label}
                      <span className="font-semibold tabular-nums">
                        {chip.value}
                      </span>
                      <InfoTooltip content={chip.tooltip} label={chip.label} />
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </details>
      )}
    </AccentCard>
  );
}

ToolCallVerdictCard.displayName = 'ToolCallVerdictCard';
